import { useAtomValue, useSetAtom } from 'jotai';
import { debounce } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';
import {
  calculateInterestsAtom,
  interestCalculationLoadingAtom,
  interestConversionDataAtom,
} from './interest-calculation-atoms';
import { InterestConversionData } from './types';

type TractFormAtom = {
  instrumentType: { value: string };
  grossAcres: { value: number | null };
  tractNetAcres: { value: number | null };
  netAcreageUnits: { value: string };
  leaseRoyaltyRate: { value: number | null };
  royaltyBurdens: { value: number | null };
  orriBurdens: { value: number | null };
};

interface UseInterestCalculationOptions {
  formFields: TractFormAtom;
  /** When true, calculations are enabled. When false, no API calls are made. */
  enabled?: boolean;
}

interface UseInterestCalculationResult {
  interestConversionData: InterestConversionData | null;
  isLoading: boolean;
}

export const DEBOUNCE_DELAY_MS = 500;

/**
 * Custom hook for managing interest calculations with debounced API calls.
 * Skips calculation on first render - only calculates when form values change.
 * In edit mode, persisted data from bundleOfSticksDetailsAtom is shown via interestDataAtom.
 * @param {UseInterestCalculationOptions} options - The options containing form fields to watch
 * @return {UseInterestCalculationResult} The calculation data and loading state
 */
export const useInterestCalculation = ({
  formFields,
  enabled = true,
}: UseInterestCalculationOptions): UseInterestCalculationResult => {
  const calculateInterests = useSetAtom(calculateInterestsAtom);
  const interestConversionData = useAtomValue(interestConversionDataAtom);
  const isLoading = useAtomValue(interestCalculationLoadingAtom);

  const isFirstRender = useRef(true);

  const debouncedCalculation = useMemo(
    () => debounce(() => calculateInterests(), DEBOUNCE_DELAY_MS),
    [calculateInterests]
  );

  useEffect(() => {
    if (!enabled) return;

    // Skip first render - only calculate when form values actually change
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    debouncedCalculation();
  }, [
    formFields.instrumentType.value,
    formFields.grossAcres.value,
    formFields.tractNetAcres.value,
    formFields.netAcreageUnits.value,
    formFields.leaseRoyaltyRate.value,
    formFields.royaltyBurdens.value,
    formFields.orriBurdens.value,
    debouncedCalculation,
    enabled,
  ]);

  useEffect(() => {
    return () => {
      debouncedCalculation.cancel();
    };
  }, [debouncedCalculation]);

  return {
    interestConversionData,
    isLoading,
  };
};
